// packages/core/src/learning/school-modules/boglehead-indexing.ts
// ─────────────────────────────────────────────────────────────────────────
// LENS PACK — Boglehead Indexing.  surface: 'school'.  PILOT / WORKED TEMPLATE.
// 5 school-core lessons, 0 extras (major school). Authored via the Lens Pack
// prompt (v2, 520-word audio floor), parsed + validated against
// LearningTypes + ApplyActionRegistry. Audio scripts 517–556 words (inside the
// curriculum validator's 500–900 band).
//
// LOCKED INVARIANTS (validateLensPacks enforces all of these):
//   id 'school-boglehead-indexing' · surface 'school' · homeSchool
//   'boglehead-indexing' · no assessment · 3 flashcards/role per lesson ·
//   one live lens-lesson applyAction · numbered persistent lesson ids.
//
// Parser-filled fields (not emitted by prompt): pillar, schoolsOfThought,
// estimated minutes, tags (slug as metadata), empty prereq/related, revision 1.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule, Lesson } from '../LearningTypes';

const LESSONS: Lesson[] = [
	{
		id: 'sch-boglehead-indexing-01',
		title: 'Boring is the point',
		description:
			'This lesson teaches why Boglehead Indexing sees boring simplicity as a strength, not a lack of ambition.',
		pillar: 'investing-for-builders',
		schoolsOfThought: ['boglehead-indexing'],
		estimatedReadMinutes: 3,
		estimatedListenMinutes: 4,
		difficulty: 2,
		tags: ['boring-is-the-point'],
		prerequisites: [],
		relatedLessons: [],
		lessonKind: 'school-core',
		concepts: [
			'boring-as-discipline',
			'humility-before-prediction',
			'broad-market-trust',
		],
		blocks: [
			{
				type: 'paragraph',
				text: 'Most people are taught to think investing has to feel smart, active, and impressive. Boglehead Indexing notices a different problem: the more investing becomes entertainment, the easier it is to make expensive mistakes.',
			},
			{
				type: 'paragraph',
				text: 'This school believes a simple, broad, low-cost approach can protect everyday investors from trying to outguess a system that is already full of smart people, fast information, and emotional traps.',
			},
			{
				type: 'bullet_list',
				items: [
					'Own broad markets',
					'Keep costs low',
					'Reduce tinkering',
				],
			},
			{
				type: 'callout',
				text: 'The common mistake is thinking boring means weak. Bogleheads think boring can be disciplined, repeatable, and easier to live with.',
			},
			{
				type: 'example',
				text: 'Someone checking headlines every week may constantly feel pressure to move money around. A Boglehead-style lens asks whether that activity is actually improving the future, or just creating more chances to panic, chase, or overreact.',
			},
			{
				type: 'paragraph',
				text: 'The tradeoff is emotional. This lens can feel unsatisfying when other people are talking about big winners, hot trends, or clever moves. It may underweight people who genuinely have time, skill, temperament, or a different investing purpose.',
			},
			{
				type: 'paragraph',
				text: 'Inside Nisk, this lens connects to a reflection: where is your money plan simple because it is thoughtful, and where is it complicated because you are trying to feel in control?',
			},
			{
				type: 'key_takeaway',
				text: 'Boglehead Indexing treats boring as a defense against avoidable mistakes.',
			},
		],
		audioScript:
			'Boglehead Indexing starts with a very unsexy idea: maybe investing does not need to be exciting to work.\n\nThat sounds almost insulting at first, because most money culture pushes the opposite message. People want the hot pick. The perfect entry point. The expert prediction. The secret insight. The thing that makes them feel sharper than the crowd. Investing gets framed like a game where the smartest person wins by seeing what everyone else missed.\n\nThe Boglehead lens pushes back on that entire mood. It says the market is not some easy opponent sitting there waiting for you to outthink it. Prices are shaped by millions of people, institutions, analysts, algorithms, news events, expectations, mistakes, fear, greed, and time. So before an everyday person builds their whole future around being clever, this worldview asks a hard question: what if cleverness is the trap?\n\nThat is why boring matters here. Boring does not mean careless. It does not mean lazy. It does not mean ignoring risk. In this school, boring means reducing the number of ways your future self can sabotage the plan.\n\nA Boglehead-style investor is paying attention to broad ownership, low costs, diversification, and behavior. They are not trying to make investing entertaining. They are trying to make it survivable. That is a different emotional posture. It cares less about looking smart this month and more about not doing something foolish over the next twenty years.\n\nThink about how normal investing mistakes actually happen. Someone hears a story. They get excited. They move money. Then the story cools down. They get nervous. They move again. Then the market falls. They panic. Then it recovers without them. Then they feel behind and chase the next winner. The damage is not always one giant disaster. Sometimes it is a chain of small emotional decisions that slowly break the plan.\n\nBoglehead Indexing tries to remove some of that drama. A broad, low-cost, simple system gives the investor fewer decisions to mess up. It does not remove risk. Markets can fall. Long periods can disappoint. Results are never promised. But the lens says uncertainty is exactly why humility matters.\n\nThe blind spot is that boring is not automatically right for every person. Some people have unusual goals, concentrated business wealth, tax complexity, real estate plans, or the skill and desire to analyze individual opportunities. A simple investing worldview may not fully account for all of that. It can also become too rigid if someone treats it like a religion instead of a lens.\n\nBut for a lot of adults trying to build stability while working, paying bills, managing debt, helping family, or starting late, the Boglehead idea is powerful because it lowers the ego requirement. You do not have to be a genius to participate. You do not have to predict every cycle. You do not have to win every argument online.\n\nThe real lesson is this: boring can be a form of strength. Not because it is flashy. Because it protects attention, reduces noise, and gives your future self fewer chances to turn fear or excitement into a bad decision.',
		flashcards: [
			{
				id: 'sch-boglehead-indexing-01-fc-main',
				role: 'main_idea',
				front: 'Why boring?',
				back: 'Bogleheads see boring simplicity as protection from avoidable investing mistakes.',
			},
			{
				id: 'sch-boglehead-indexing-01-fc-watch',
				role: 'watch_out',
				front: 'What is the trap?',
				back: 'Treating investing like entertainment instead of a repeatable long-term system.',
			},
			{
				id: 'sch-boglehead-indexing-01-fc-use',
				role: 'use_it',
				front: 'How do you use it?',
				back: 'Ask where complexity is helping and where it only creates more chances to overreact.',
			},
		],
		applyAction: {
			type: 'noToolAction',
			label: 'Audit your complexity',
			prompt: 'Name one part of your money plan that may be complicated because you want control, not because it truly helps.',
		},
		revision: 1,
	},
	{
		id: 'sch-boglehead-indexing-02',
		title: 'The market heard the story',
		description:
			'This lesson teaches why Boglehead Indexing is skeptical of exciting investment narratives, even when the story sounds true.',
		pillar: 'investing-for-builders',
		schoolsOfThought: ['boglehead-indexing'],
		estimatedReadMinutes: 3,
		estimatedListenMinutes: 4,
		difficulty: 3,
		tags: ['the-market-heard-the-story'],
		prerequisites: [],
		relatedLessons: [],
		lessonKind: 'school-core',
		concepts: [
			'price-contains-expectations',
			'narrative-risk',
			'diversification-as-humility',
		],
		blocks: [
			{
				type: 'paragraph',
				text: 'A great investing story can feel obvious. The company is changing the world. The sector is the future. The trend is everywhere. Boglehead Indexing asks one more question: what if the price already knows?',
			},
			{
				type: 'paragraph',
				text: 'This school believes stories are not enough, because markets often price in expectations before regular investors feel fully convinced. A true story can still lead to a disappointing result if too much optimism is already baked in.',
			},
			{
				type: 'bullet_list',
				items: [
					'Stories create confidence',
					'Prices include expectations',
					'Broad ownership reduces dependence on one story',
				],
			},
			{
				type: 'callout',
				text: 'The common mistake is confusing a good story with a good outcome.',
			},
			{
				type: 'example',
				text: 'A business can be impressive and still disappoint investors if everyone already expected greatness. Boglehead thinking does not require guessing which story wins. It spreads exposure so one narrative does not carry the whole future.',
			},
			{
				type: 'paragraph',
				text: 'The tradeoff is that broad diversification means accepting many average-looking pieces along with the winners. You may watch concentrated bets outperform for a while, and that can test patience.',
			},
			{
				type: 'paragraph',
				text: 'Inside Nisk, this lens turns story-chasing into a testable question: are you building around a durable system, or around a story that needs to be exactly right?',
			},
			{
				type: 'key_takeaway',
				text: 'Bogleheads do not reject stories. They reject betting the whole plan on one story being underpriced.',
			},
		],
		audioScript:
			'One of the easiest ways to get pulled into a bad investing decision is to hear a story that sounds true.\n\nThat is the tricky part. The story does not have to be fake. A company really can be impressive. A technology really can change behavior. A sector really can grow. A founder really can be talented. A country really can have long-term potential. The problem is that investing outcomes are not only about whether the story is true. They are also about what people already expected when you bought into the story.\n\nThat is where Boglehead Indexing gets careful. This worldview is not saying stories are useless. It is saying stories are dangerous when they make you forget price, uncertainty, and humility.\n\nWhen a story becomes obvious to everyone, the market may have already reacted. The exciting future may already be partly reflected in the price. So an investor can be right about the story and still not get the outcome they imagined. That feels unfair, but it is a basic market reality. The question is not only, “Is this good?” The question is, “What expectations are already built in?”\n\nBogleheads do not want the average person’s future to depend on answering that perfectly over and over. Instead of needing one company, one sector, one theme, or one prediction to carry the plan, this lens prefers broad ownership. You own many pieces. Some disappoint. Some surprise you. Some become bigger over time because they perform well. Some fade. You do not have to know every winner in advance.\n\nThis is diversification as humility. Not diversification as a magic shield. Broad ownership does not remove market risk. A broad market can still fall. Bad decades can happen. Inflation, recessions, job loss, and personal emergencies still matter. But diversification reduces the pressure to make one narrow story do all the work.\n\nThe common mistake is thinking a broad approach means you are ignoring the future. Actually, this school is trying to respect the fact that the future is messy. It is saying, “I may not know which story wins, so I will not build my entire plan around pretending I do.”\n\nThe emotional tradeoff is real. Stories are exciting. Broad ownership is not. When someone else is concentrated in the thing that is running hot, boring diversification can feel like losing. You may feel slow. You may feel average. You may feel like you are missing the party. That feeling is one of the hardest parts of this lens.\n\nBut the Boglehead answer is that every party has a cost if you enter late, overpay, or cannot handle the downturn. A strong narrative can become a trap because it makes risk feel invisible.\n\nInside Nisk, this lesson matters because it changes the question. Instead of asking, “What is the hottest story?” you ask, “How much of my plan depends on this story being right, and what happens if it is wrong?” That is a calmer question. It is less glamorous, but it is often more useful.\n\nThe point is not to never care about companies, innovation, or trends. The point is to avoid confusing belief with certainty. Boglehead Indexing is paying attention to how easily a good story can become an overconfident bet.',
		flashcards: [
			{
				id: 'sch-boglehead-indexing-02-fc-main',
				role: 'main_idea',
				front: 'Why doubt stories?',
				back: 'A true story can still disappoint if high expectations are already priced in.',
			},
			{
				id: 'sch-boglehead-indexing-02-fc-watch',
				role: 'watch_out',
				front: 'What is narrative risk?',
				back: 'Letting an exciting story make uncertainty and price feel less important.',
			},
			{
				id: 'sch-boglehead-indexing-02-fc-use',
				role: 'use_it',
				front: 'What question helps?',
				back: 'Ask how much your plan depends on one story being right.',
			},
		],
		applyAction: {
			type: 'openSimulator',
			label: 'Test a less story-driven path',
		},
		revision: 1,
	},
	{
		id: 'sch-boglehead-indexing-03',
		title: 'Costs are quiet damage',
		description:
			'This lesson teaches why Boglehead Indexing treats fees and friction as controllable leaks that can quietly weaken long-term outcomes.',
		pillar: 'investing-for-builders',
		schoolsOfThought: ['boglehead-indexing'],
		estimatedReadMinutes: 3,
		estimatedListenMinutes: 4,
		difficulty: 2,
		tags: ['costs-are-quiet-damage'],
		prerequisites: [],
		relatedLessons: [],
		lessonKind: 'school-core',
		concepts: ['quiet-cost-drag', 'controllable-friction', 'fee-awareness'],
		blocks: [
			{
				type: 'paragraph',
				text: 'A fee does not usually feel dramatic. It does not scream like a crash or sting like a bad trade. It just quietly takes a small piece of the future again and again.',
			},
			{
				type: 'paragraph',
				text: 'Boglehead Indexing pays attention to costs because costs are one of the few investing variables regular people can actually control. Future returns are uncertain. Predictions are uncertain. Fees are visible.',
			},
			{
				type: 'bullet_list',
				items: [
					'Expense ratios',
					'Trading friction',
					'Advice or platform costs',
				],
			},
			{
				type: 'callout',
				text: 'The common mistake is dismissing small percentages because they look harmless in one year.',
			},
			{
				type: 'example',
				text: 'A recurring cost that looks tiny can become meaningful over decades because it removes money that could have stayed invested. The hidden damage is not just the fee itself; it is the growth that fee never gets to earn.',
			},
			{
				type: 'paragraph',
				text: 'The tradeoff is that not every cost is automatically bad. Some people pay for planning, tax help, accountability, or structure. Boglehead thinking is not “never pay.” It is “know what you are paying and what problem it solves.”',
			},
			{
				type: 'paragraph',
				text: 'Inside Nisk, this lens connects costs to freedom: every quiet leak deserves a second look because it may be taking options from your future self.',
			},
			{
				type: 'key_takeaway',
				text: 'Bogleheads obsess over costs because small leaks can become large future tradeoffs.',
			},
		],
		audioScript:
			'Boglehead Indexing pays a lot of attention to fees because fees are sneaky.\n\nThey do not feel like a crisis. A market crash feels obvious. A bad trade feels obvious. A job loss feels obvious. But a fee often feels tiny, normal, and harmless. It sits in the background. It does not send you an angry notification. It does not make you feel like you made a dramatic mistake. It just quietly takes a piece of the money that could have stayed working for your future.\n\nThat is why this worldview cares. Bogleheads are not obsessed with costs because costs are the only thing that matter. They care because costs are one of the few variables an everyday investor can actually control.\n\nYou cannot control what the market does next year. You cannot control whether a recession happens. You cannot control whether an expert prediction turns out right. You cannot control whether one sector becomes popular or hated. But you can often see what you are paying. Fund costs. Trading costs. Platform costs. Advisory costs. Tax friction. Product fees. Complexity fees. Those are not always easy to understand, but they are more visible than the future.\n\nThe common mistake is looking at a small percentage and thinking, “That is nothing.” And in one year, maybe it does not feel like much. But long-term investing is a compounding game, and recurring costs compound in the wrong direction. The cost does not only remove money today. It removes the future growth that money might have produced.\n\nThat is the part people miss. A fee is not just a fee. It is a little piece of future optionality getting shaved off. Over a long enough period, those shavings can matter.\n\nBoglehead Indexing also sees cost as a marketing trap. Expensive financial products often come dressed up in confidence. There may be fancy charts, polished language, exclusive access, complicated strategies, or a feeling that you are doing something more advanced than the average person. Sometimes there is real value. Sometimes you are simply paying more to feel sophisticated.\n\nThe Boglehead lens asks a blunt question: what job is this cost doing?\n\nIs it helping you behave better? Is it giving you planning support you actually need? Is it solving a tax, estate, business, or emotional problem? Is it preventing a bigger mistake? Or is it just drag?\n\nThat distinction matters because this school is not saying every paid service is bad. Some people benefit from advice, coaching, planning, or structure. A low-cost plan that a person cannot understand or follow may not work for them. Human behavior matters. Real life matters.\n\nThe blind spot is that Boglehead cost-awareness can become too narrow if it treats “cheapest” as automatically best. Cheapest is not always the same as most useful. The better lens is value against cost.\n\nInside Nisk, this lesson should make you slower around anything that quietly takes from your future. The goal is not paranoia. The goal is awareness. If a cost is worth it, name why. If it is not worth it, do not let it hide behind small percentages and complicated language.\n\nThe Boglehead move is simple: before chasing more return, check what is leaking.',
		flashcards: [
			{
				id: 'sch-boglehead-indexing-03-fc-main',
				role: 'main_idea',
				front: 'Why do costs matter?',
				back: 'Costs are controllable leaks that can reduce long-term compounding.',
			},
			{
				id: 'sch-boglehead-indexing-03-fc-watch',
				role: 'watch_out',
				front: 'Why are fees sneaky?',
				back: 'Small percentages feel harmless short term but can grow into meaningful future drag.',
			},
			{
				id: 'sch-boglehead-indexing-03-fc-use',
				role: 'use_it',
				front: 'What should you ask?',
				back: 'Ask what problem each cost solves and whether the value is worth the drag.',
			},
		],
		applyAction: {
			type: 'runFreedomCost',
			label: 'Test a quiet money leak',
		},
		revision: 1,
	},
	{
		id: 'sch-boglehead-indexing-04',
		title: 'Behavior beats brilliance',
		description:
			'This lesson teaches why Boglehead Indexing worries more about emotional mistakes than finding the perfect investment idea.',
		pillar: 'investing-for-builders',
		schoolsOfThought: ['boglehead-indexing'],
		estimatedReadMinutes: 3,
		estimatedListenMinutes: 4,
		difficulty: 2,
		tags: ['behavior-beats-brilliance'],
		prerequisites: [],
		relatedLessons: [],
		lessonKind: 'school-core',
		concepts: [
			'investor-behavior-risk',
			'emotional-timing',
			'plan-survivability',
		],
		blocks: [
			{
				type: 'paragraph',
				text: 'A smart plan that you abandon under pressure is not really your plan. Boglehead Indexing notices that the investor often becomes the weakest part of the system.',
			},
			{
				type: 'paragraph',
				text: 'This school believes behavior can matter more than brilliance. Fear, envy, boredom, and overconfidence can damage results even when the original idea was reasonable.',
			},
			{
				type: 'bullet_list',
				items: [
					'Panic selling',
					'Performance chasing',
					'Constant strategy switching',
				],
			},
			{
				type: 'callout',
				text: 'The common mistake is blaming the market when the real issue is a plan the user could not emotionally survive.',
			},
			{
				type: 'example',
				text: 'Someone watches markets fall, feels panic, moves to safety, then watches a recovery happen without them. Another person sees a hot trend, buys late, then loses patience when it cools. The pattern is emotional timing, not just bad luck.',
			},
			{
				type: 'paragraph',
				text: 'The tradeoff is that a behavior-friendly plan may feel less optimized. It may give up the thrill of chasing a more exciting opportunity in exchange for something easier to stay with.',
			},
			{
				type: 'paragraph',
				text: 'Inside Nisk, this lens asks a practical question: what plan could your real self follow, not your imaginary calm self?',
			},
			{
				type: 'key_takeaway',
				text: 'Bogleheads care about behavior because the best plan on paper can fail in a real human nervous system.',
			},
		],
		audioScript:
			'Boglehead Indexing has a simple but uncomfortable belief: the investor is often more dangerous than the investment.\n\nThat sounds harsh, but it is not meant as an insult. It is meant as a realistic view of human behavior. People are not spreadsheets. People get scared. People get impatient. People compare themselves to friends. People see someone online making fast money and feel behind. People watch markets fall and feel like the world changed forever. People tell themselves they are long-term thinkers, then panic when long-term investing starts feeling painful.\n\nThis school pays attention to that gap between the plan and the person using the plan.\n\nA plan can look smart on paper and still fail in real life if the person cannot stay with it. That is why Bogleheads care so much about simplicity, diversification, and low tinkering. Not because those things remove all risk. They do not. But they can reduce the number of emotional decisions you have to make.\n\nThe common pattern is easy to recognize. Someone starts with a plan. Then a hot trend appears. They feel like they are missing out. They move money. Then the trend cools. They feel foolish. They change again. Then markets fall. They panic. They move again. Then markets recover. They re-enter later, frustrated and less confident. Over time, the damage is not just market movement. The damage is the behavior loop.\n\nBoglehead Indexing tries to build around that human weakness. It says, “What if the plan should be designed for your real behavior, not your fantasy behavior?”\n\nYour fantasy self is calm during crashes. Your fantasy self ignores hype. Your fantasy self never checks returns too much. Your fantasy self never panic sells, never revenge trades, never chases, never gets jealous, never changes strategy because of a podcast. Your real self may be different. Boglehead thinking respects that difference.\n\nThis is where the worldview gets practical. A less exciting plan that someone can actually follow may be more useful than a brilliant plan that collapses under emotion. The point is not to remove thinking. The point is to remove unnecessary decision points where fear and ego can hijack the system.\n\nThe tradeoff is that this can feel less optimized. Some investors want more control, more analysis, more concentration, or more active decision-making. Some may have the temperament and skill for that. Boglehead Indexing can feel too passive to them. It may underweight the satisfaction some people get from studying businesses or building a more custom approach.\n\nBut for many adults, the big risk is not that they fail to find the perfect idea. The big risk is that they cannot stay consistent through ugly periods.\n\nInside Nisk, this lesson changes the question. Instead of asking, “What is the smartest plan?” ask, “What plan can I still follow when I am stressed, tired, scared, busy, or jealous?” That question is less glamorous, but it is closer to real life.\n\nBoglehead Indexing is not just an investing philosophy. It is a behavior philosophy. It tries to protect your future from the version of you that makes emotional decisions at the worst possible time.',
		flashcards: [
			{
				id: 'sch-boglehead-indexing-04-fc-main',
				role: 'main_idea',
				front: 'What is behavior risk?',
				back: 'The risk that emotion makes you abandon or damage a reasonable plan.',
			},
			{
				id: 'sch-boglehead-indexing-04-fc-watch',
				role: 'watch_out',
				front: 'What breaks plans?',
				back: 'Panic, envy, boredom, overconfidence, and constant strategy switching.',
			},
			{
				id: 'sch-boglehead-indexing-04-fc-use',
				role: 'use_it',
				front: 'What question helps?',
				back: 'Ask what plan your real self can follow under pressure.',
			},
		],
		applyAction: {
			type: 'noToolAction',
			label: 'Name your trigger',
			prompt: 'Write down which emotion is most likely to make you change your money plan too quickly.',
		},
		revision: 1,
	},
	{
		id: 'sch-boglehead-indexing-05',
		title: 'When simple gets too rigid',
		description:
			'This lesson teaches the blind spot of Boglehead Indexing: simplicity helps, but one clean investing philosophy cannot solve every life situation.',
		pillar: 'investing-for-builders',
		schoolsOfThought: ['boglehead-indexing'],
		estimatedReadMinutes: 3,
		estimatedListenMinutes: 4,
		difficulty: 3,
		tags: ['when-simple-gets-too-rigid'],
		prerequisites: [],
		relatedLessons: [],
		lessonKind: 'school-core',
		concepts: [
			'simplicity-blind-spot',
			'life-context-fit',
			'strategy-humility',
		],
		blocks: [
			{
				type: 'paragraph',
				text: 'Every strong money worldview can become a trap when people turn it into an identity. Boglehead Indexing is powerful, but it can still become too rigid.',
			},
			{
				type: 'paragraph',
				text: 'This school sees the value of simplicity, diversification, low costs, and steady behavior. The blind spot appears when users act like a clean investing answer automatically fits every person, goal, and season of life.',
			},
			{
				type: 'bullet_list',
				items: [
					'Unstable income',
					'Family obligations',
					'Short-term goals',
				],
			},
			{
				type: 'callout',
				text: 'The common mistake is turning “simple is often useful” into “simple always solves it.”',
			},
			{
				type: 'example',
				text: 'Someone with steady income and a long time horizon may find a simple broad-market approach easy to live with. Someone supporting family, managing debt stress, building a business, or planning a near-term move may need a wider lens than portfolio simplicity.',
			},
			{
				type: 'paragraph',
				text: 'The tradeoff is that Boglehead thinking can underweight personalization, liquidity needs, emotional fear, taxes, existing concentration, and non-investing goals. A clean portfolio does not automatically mean a clean life.',
			},
			{
				type: 'paragraph',
				text: 'Inside Nisk, the mature move is to borrow the humility without turning the lens into a commandment. Test how the worldview fits your actual numbers, timeline, and obligations.',
			},
			{
				type: 'key_takeaway',
				text: 'Boglehead Indexing works best as a humility tool, not a one-size-fits-all identity.',
			},
		],
		audioScript:
			'The danger with any useful money philosophy is that people eventually start treating it like a religion.\n\nBoglehead Indexing is no exception. It has real strengths. It protects against overconfidence. It pushes back against high costs. It respects diversification. It reduces unnecessary tinkering. It reminds regular people that they do not have to become market geniuses to build a serious financial life.\n\nThat is all valuable.\n\nBut a lens is still a lens. It helps you see certain things clearly, and it can make other things easier to ignore.\n\nThe Boglehead blind spot appears when simplicity becomes rigidity. There is a big difference between saying, “Simple is often powerful,” and saying, “A simple broad-market investing approach solves every financial situation.” The first statement is a useful principle. The second statement can become lazy.\n\nReal life is messier than a portfolio. People have unstable income. Family obligations. Debt anxiety. Immigration issues. Healthcare costs. Short-term housing goals. Business ownership. Concentrated stock from work. Parents to support. Kids to plan for. Fear from past mistakes. A near-term move. A partner with a different risk tolerance. A career change. A season where cash and runway matter more than long-term growth.\n\nA clean investing philosophy cannot automatically solve all of that.\n\nThis is where Boglehead Indexing needs humility about itself. Its strength is that it simplifies a noisy part of life. Its weakness is that it can tempt people to oversimplify the rest of life too.\n\nFor example, someone with stable income, no high-interest debt pressure, a long time horizon, and a calm temperament may find this lens fits beautifully. They may not need much complexity. But someone whose income swings wildly may need more attention on cash reserves and flexibility. Someone building a business may already be taking concentrated risk outside a portfolio. Someone close to a major life decision may care more about liquidity than long-term market exposure for a season.\n\nThat does not mean the Boglehead lens is wrong. It means context matters.\n\nThere is also an emotional blind spot. Boglehead language can sometimes sound like, “Just stay the course.” That may be directionally useful, but it can feel too simple for someone who is genuinely scared. People do not only need correct principles. They need a plan they can emotionally hold. A technically clean plan that someone abandons under stress is not clean in practice.\n\nAnother blind spot is that some people do have different goals. They may want to study businesses. Build real estate. Run a company. Hold more cash for personal reasons. Take calculated risks. Optimize taxes. Design a life around flexibility rather than maximum portfolio simplicity. Boglehead Indexing does not have to explain every valid path.\n\nInside Nisk, that is why Schools of Thought matter. A school is not a commandment. It is a way of seeing. The mature move is to borrow what the lens does well: humility, cost awareness, broad diversification, suspicion of hype, and respect for behavior. Then test it against your actual life.\n\nThe lesson is not “Bogleheads are always right.” The lesson is “this lens protects against real mistakes, but it still needs context.”\n\nUsed well, Boglehead Indexing makes you calmer and harder to manipulate. Used poorly, it can make you dismiss details that genuinely matter. The difference is whether you treat it as a tool or an identity.',
		flashcards: [
			{
				id: 'sch-boglehead-indexing-05-fc-main',
				role: 'main_idea',
				front: 'What is the blind spot?',
				back: 'Simplicity can become too rigid when it ignores real-life context.',
			},
			{
				id: 'sch-boglehead-indexing-05-fc-watch',
				role: 'watch_out',
				front: 'What is the misuse?',
				back: 'Treating a useful investing lens like a universal answer for every money problem.',
			},
			{
				id: 'sch-boglehead-indexing-05-fc-use',
				role: 'use_it',
				front: 'How do you apply it?',
				back: 'Borrow the humility, then test whether the lens fits your actual life.',
			},
		],
		applyAction: {
			type: 'noToolAction',
			label: 'Check the fit',
			prompt: 'Name one part of your real life that a simple investing lens may not fully account for.',
		},
		revision: 1,
	},
];

export const SCHOOL_BOGLEHEAD_INDEXING_MODULE: LearningModule = {
	id: 'school-boglehead-indexing',
	title: 'The Boglehead Lens',
	description:
		'How Boglehead indexing interprets investing — why it trusts boring, low-cost, behavior-first investing over cleverness.',
	emoji: '🐢',
	accentColor: '#c8ff00',
	tier: 'advanced',
	pillar: 'investing-for-builders',
	order: 1,
	surface: 'school',
	homeSchool: 'boglehead-indexing',
	lessons: LESSONS,
};
